import { trackConfig, type IFramework } from '@/framework';
import { type TextSelectionRange } from '@/utils/text-kit';
import { LayerType, type ILayerProps, type ITextLayerProps, type Optional } from '@/version-types';
import {
    applyTextRangeAttrs,
    getLayerExtraInfo,
    pushHistory,
    requestTextSVGPreview,
    unifyTextContentsAttrs,
    updateLayer,
    validateInputLayer,
} from '../utils';
import { type IShapeText } from './shape-text';

const COLOR_FUNC_ID = trackConfig[LayerType.TEXT_LAYER].color;

export class FillColor {
    private prevLayer: ILayerProps | null = null;

    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
    ) {}

    private prepare(
        prevLayer: ILayerProps,
        color: string,
        range?: Optional<TextSelectionRange>,
    ): { layer: ILayerProps; range: TextSelectionRange | null } {
        const prevLayerData = prevLayer.data as ITextLayerProps;
        let nextLayerData: ITextLayerProps = { ...prevLayerData, textGradient: null, texture: undefined };

        if (range && range.start !== range.end) {
            const result = applyTextRangeAttrs(prevLayerData, range, {
                key: 'color',
                value: color,
            });

            const nextLayer: ILayerProps = {
                ...prevLayer,
                data: { ...nextLayerData, textContents: result.textContents },
            };
            return { layer: nextLayer, range: result.range };
        }

        nextLayerData = { ...nextLayerData, color };

        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const { textContents: prevTextContents = [] } = prevLayerData;
        if (prevTextContents.length > 0) {
            const nextTextContents = unifyTextContentsAttrs(prevTextContents, { color });
            nextLayerData = { ...nextLayerData, textContents: nextTextContents };
            nextLayer = { ...nextLayer, data: nextLayerData };
        }

        // 若当前图层是花字场景，需要将修改的颜色同步到该字段中
        if (prevLayerData.advancedTextMaterial) {
            const extraInfo = getLayerExtraInfo(prevLayer);
            extraInfo.overrideColorInArtTextCase = color;
            nextLayer = { ...nextLayer, extraInfo };
        }

        return { layer: nextLayer, range: null };
    }

    private async setSingle(
        prevLayer: ILayerProps,
        color: string,
        options: { range?: Optional<TextSelectionRange>; writeHistory?: boolean },
    ) {
        const { range = null, writeHistory = false } = options;
        const result = this.prepare(prevLayer, color, range);
        let nextLayer = result.layer;

        const { shapeExtension } = prevLayer.data as ITextLayerProps;
        if (shapeExtension && writeHistory) {
            const layer = await this.shapeText.refreshShapeTextRender(nextLayer);
            if (layer) nextLayer = layer;
        }

        if (writeHistory) requestTextSVGPreview(this.framework, 'commit', nextLayer);
        else if (!shapeExtension) requestTextSVGPreview(this.framework, 'update', nextLayer);
        updateLayer(this.framework, nextLayer, { activate: true, dataOnly: !shapeExtension && !writeHistory });
        if (writeHistory) {
            pushHistory(this.framework, prevLayer, nextLayer, COLOR_FUNC_ID);
            this.prevLayer = null;
        }

        return { layer: nextLayer, range: result.range };
    }

    private setGroup(prevLayer: ILayerProps, color: string, writeHistory: boolean) {
        const children = prevLayer.children;
        if (children.length === 0) return;

        const layers = children.map((child) => this.prepare(child, color, null).layer);
        const nextLayer: ILayerProps = { ...prevLayer, children: layers };

        requestTextSVGPreview(this.framework, writeHistory ? 'commit' : 'update', nextLayer);
        updateLayer(this.framework, nextLayer, { activate: true, dataOnly: !writeHistory });
        if (writeHistory) {
            pushHistory(this.framework, prevLayer, nextLayer, COLOR_FUNC_ID);
            this.prevLayer = null;
        }

        return { layer: nextLayer, range: null };
    }

    async setFillColor(
        layerID: string,
        color: string,
        options: { range?: Optional<TextSelectionRange>; writeHistory?: boolean } = {},
    ) {
        const prevLayer = (() => {
            if (this.prevLayer && this.prevLayer.id === layerID) return this.prevLayer;
            this.prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
            return this.prevLayer;
        })();

        if (!validateInputLayer(prevLayer)) return;

        const { range = null, writeHistory = false } = options;
        const result =
            prevLayer.layerType === LayerType.GROUP_LAYER
                ? this.setGroup(prevLayer, color, writeHistory)
                : await this.setSingle(prevLayer, color, { range, writeHistory });

        return result;
    }
}
